import { FormationDataProvider } from '../../data/formations/FormationsData';
import { FormationsTemplates } from '../templates/FormationsTemplates';
import { FormationsEffects } from '../../utils/formationsEffects';
import { FormationInscriptionManager } from '../forms/FormationInscriptionForm';
import { isEnrollmentEnabled } from '../../utils/flags';
import '../../styles/components/formations.css';
import '../../styles/components/formations-effects.css';
import '../../styles/components/shared-sections.css';
import '../../styles/components/formation-inscription.css';


export default class Formations {
  private element: HTMLElement;
  private cleanup: Array<() => void> = [];

  constructor() {
    // Créer l'élément principal de la section
    this.element = document.createElement('section');
    this.element.id = 'formations';
    this.element.className = 'formations-section py-24 bg-gradient-to-b from-[#0b1220]/70 via-[#0b1220]/80 to-[#0b1220]/90 relative overflow-hidden';
    // Rendu initial
    this.render();
    
    // Configuration des événements et animations
    this.setupInteractions();
  }

  /**
   * Génère le rendu HTML de la section Formations
   */
  private render(): void {
    // Récupérer les données des formations
    const formations = FormationDataProvider.getFormationData();
    
    // Ajouter le fond dynamique
    const backgroundElement = document.createElement('div');
    backgroundElement.innerHTML = FormationsTemplates.generateBackgroundHTML();
    this.element.appendChild(backgroundElement.firstElementChild as HTMLElement);
    
    // Créer le conteneur principal
    const container = document.createElement('div');
    container.className = 'container relative z-10 flex flex-col items-center';
    
    // Ajouter le titre de la section
    const titleSection = document.createElement('div');
    titleSection.className = 'text-center mb-16 flex flex-col items-center justify-center w-full';
    titleSection.style.display = 'flex';
    titleSection.style.flexDirection = 'column';
    titleSection.style.alignItems = 'center';
    titleSection.innerHTML = FormationsTemplates.generateTitleSectionHTML();
    
    // Ajouter les onglets de navigation
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'formation-tabs-container relative z-10 mb-12 fade-in-up delay-500 mx-auto';
    tabsContainer.innerHTML = FormationsTemplates.generateTabsHTML(formations);
    
    
    // Ajouter le conteneur pour le contenu des formations
    const contentContainer = document.createElement('div');
    contentContainer.className = 'formation-content-container mt-8 relative min-h-[500px] transition-all duration-500';
    
    // Générer le contenu pour chaque formation
    formations.forEach(formation => {
      const formationContent = document.createElement('div');
      formationContent.innerHTML = FormationsTemplates.generateFormationContentHTML(formation);
      contentContainer.appendChild(formationContent.firstElementChild as HTMLElement);
    });
    
    // Ajouter le CTA (Call to Action)
    const ctaSection = document.createElement('div');
    ctaSection.innerHTML = FormationsTemplates.generateCtaSectionHTML();

    // Appends sans conteneur verre
    container.appendChild(titleSection);
    container.appendChild(tabsContainer);
    container.appendChild(contentContainer);
    container.appendChild(ctaSection);
    
    // Ajouter le conteneur au composant principal
    this.element.appendChild(container);
  }

  /**
   * Configure les interactions et animations de la section
   */
  private setupInteractions(): void {
    // Attendre que le DOM soit complètement prêt avant d'initialiser les événements
    setTimeout(() => {
      // Configurer le système d'onglets
      const tabCleanup = FormationsEffects.setupTabSystem(this.element);
      this.cleanup.push(tabCleanup);
      
      // Configurer les animations au défilement
      const scrollCleanup = FormationsEffects.setupScrollAnimations(this.element);
      this.cleanup.push(scrollCleanup);
      
      // Configurer l'effet de parallaxe
      const parallaxCleanup = FormationsEffects.setupParallaxEffect(this.element);
      this.cleanup.push(parallaxCleanup);
      
      // Configurer les boutons d'achat (selon le flag)
      if (isEnrollmentEnabled()) {
        this.setupPurchaseButtons();
      } else {
        // Quand l'inscription en ligne est désactivée, s'assurer que les anciens boutons ne déclenchent rien
        const purchaseButtons = this.element.querySelectorAll('.btn-formation-purchase');
        purchaseButtons.forEach(btn => {
          (btn as HTMLButtonElement).disabled = true;
          (btn as HTMLButtonElement).setAttribute('aria-disabled', 'true');
          btn.classList.add('opacity-60', 'cursor-not-allowed');
          btn.setAttribute('title', 'Inscriptions en ligne bientôt disponibles');
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            window.location.href = 'contact.html';
          }, { once: true });
        });
      }

      // Délégation d'événements (sécurité si le DOM est regénéré)
      const delegated = (event: Event) => {
        const target = event.target as HTMLElement;
        const btn = target?.closest?.('.btn-formation-purchase') as HTMLButtonElement | null;
        if (!btn || !this.element.contains(btn)) return;
        event.preventDefault();
        if (!isEnrollmentEnabled()) {
          window.location.href = 'contact.html';
          return;
        }
        console.log('[Formations] Click delegated on purchase button');

        const formationId = btn.dataset.formation;
        const formationName = btn.dataset.formationName;
        const formationPrice = btn.dataset.formationPrice;
        if (!formationId || !formationName || !formationPrice) return;

        const inscriptionForm = FormationInscriptionManager.createInscriptionModal({
          id: formationId,
          name: formationName,
          price: formationPrice
        });
        inscriptionForm.showModal();
        console.log('[Formations] Modal should be visible now (delegated)');
      };
      document.addEventListener('click', delegated);
      this.cleanup.push(() => document.removeEventListener('click', delegated));
    }, 300);
  }
  
  /**
   * Configure les boutons d'achat des formations
   */
  private setupPurchaseButtons(): void {
    const purchaseButtons = this.element.querySelectorAll('.btn-formation-purchase');
    
    purchaseButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        // Empêche le gestionnaire délégué global de déclencher une deuxième ouverture
        e.stopPropagation();
        
        const btn = e.currentTarget as HTMLButtonElement;
        const formationId = btn.dataset.formation;
        const formationName = btn.dataset.formationName;
        const formationPrice = btn.dataset.formationPrice;
        
        if (!formationId || !formationName || !formationPrice) return;
        if (!isEnrollmentEnabled()) {
          window.location.href = 'contact.html';
          return;
        }
        
        // Créer et afficher le formulaire d'inscription modal
        const inscriptionForm = FormationInscriptionManager.createInscriptionModal({
          id: formationId,
          name: formationName,
          price: formationPrice
        });
        
        // Afficher le formulaire en modal
        inscriptionForm.showModal();
      });
    });
  }

  /**
   * Retourne l'élément HTML de la section
   */
  public getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Nettoie les ressources lors de la destruction du composant
   */
  public destroy(): void {
    // Appeler toutes les fonctions de nettoyage
    this.cleanup.forEach(fn => fn());
    this.cleanup = [];
  }
}
